/**
 * Collection-schedule rules — logic THUẦN (không framework, không DB) cho thu định kỳ
 * (US-E6.F1-0035 · @UC0038, Phase 7 Collections). Trái tim quyết định:
 *  - validate số tiền/người > 0 (BigInt minor — B1, KHÔNG float).
 *  - validate ngày bắt đầu KHÔNG quá khứ theo TIMEZONE group locale (PO §6 — mặc định Asia/Ho_Chi_Minh).
 *  - derive `periodKey` của đợt đầu theo tần suất (idempotency: identity đợt = scheduleId+periodKey).
 *  - build `recurrence` JSON lưu vào CollectionSchedule (weekly → kèm thứ).
 *  - quyết định "đã pause thì KHÔNG sinh đợt" (generateInstanceIfDue skip).
 *
 * Pure ⇒ tái dùng giữa api (sinh đợt đầu đồng bộ) và worker P8.5 (sinh định kỳ) — DRY công thức (B3).
 * Ném DomainError mang `code` ổn định → API map sang 400/422.
 */
import { DomainError } from "./errors.js";
// Module LÁ (chỉ phụ thuộc `errors.js`) — dùng chung với `semester.ts` mà KHÔNG tạo vòng ESM.
// `semester.ts` import ngược file này (`localDateParts`), nên import thẳng `semester.js` ở đây sẽ
// đóng vòng; `tsc` không bắt được, chỉ nổ lúc chạy.
import { assertSemesterStartMonths } from "./semester-months.js";

/** Tần suất thu định kỳ hợp lệ (khớp Collection.recurrence.freq + DTO biên). */
export const COLLECTION_FREQUENCIES = [
  "weekly",
  "monthly",
  "quarterly",
  "yearly",
  // US-E23.F3-0333-S07 (@UC9531): năm học — ĐƠN VỊ lặp có KHOẢNG TRỐNG (bỏ tháng hè), không diễn
  // đạt được bằng {monthly, interval:N}. Mốc kỳ do `schoolYearPeriods` sinh, không do cuộn đều đặn.
  "school_year",
  // 🔴 US-E16.F3-0688 (@UC0050 · QĐ người dùng 03/09/2026): HỌC KỲ — **2–3 kỳ/năm**, mỗi học kỳ MỘT
  // lần. KHÁC `school_year` ngay trên (9 kỳ/năm, mỗi THÁNG một kỳ): khác ĐƠN VỊ lặp, không phải khác
  // tham số — nên là giá trị enum riêng, không phải `{school_year, interval:N}`. Mốc do
  // `semesterPeriods` (semester.ts) sinh từ mốc tháng NGƯỜI TẠO chọn, lưu theo từng đợt thu.
  "semester",
] as const;

export type CollectionFrequency = (typeof COLLECTION_FREQUENCIES)[number];

/**
 * Trạng thái lịch thu — active|paused (Pause/Resume — 0035 AC5) + cancelled (US-E16.F3-0184 huỷ hẳn,
 * KHÔNG sinh kỳ tương lai, resume ⇒ 409). Danh sách này = nguồn CHECK DB (drift test đối chiếu).
 */
export const SCHEDULE_STATUS = {
  ACTIVE: "active",
  PAUSED: "paused",
  CANCELLED: "cancelled",
} as const;

export type ScheduleStatus = (typeof SCHEDULE_STATUS)[keyof typeof SCHEDULE_STATUS];

/** Danh sách trạng thái lịch hợp lệ (= whitelist CHECK collection_schedules_status_check). */
export const SCHEDULE_STATUSES = Object.values(SCHEDULE_STATUS) as readonly ScheduleStatus[];

/** Timezone mặc định khi group chưa cấu hình locale (PO §6 — "quá khứ" tính theo giờ VN). */
export const DEFAULT_GROUP_TIMEZONE = "Asia/Ho_Chi_Minh";

/** Thông điệp lỗi user-facing (một nguồn sự thật api ↔ mobile — khớp AC2/AC4). */
export const AMOUNT_NOT_POSITIVE_MESSAGE = "Số tiền phải lớn hơn 0";
export const START_DATE_IN_PAST_MESSAGE = "Ngày bắt đầu không được là ngày trong quá khứ";

export function isCollectionFrequency(value: unknown): value is CollectionFrequency {
  return (COLLECTION_FREQUENCIES as readonly string[]).includes(value as string);
}

/**
 * Validate số tiền/người (minor unit BigInt) phải > 0 (AC4). ≤ 0 ⇒ DomainError
 * LEDGER_AMOUNT_NOT_POSITIVE (httpStatus 422) với message user-facing AMOUNT_NOT_POSITIVE_MESSAGE.
 * B1: nhận BigInt (KHÔNG float). Trả lại chính giá trị để dùng tiếp (fluent).
 */
export function assertAmountPerMemberPositive(amountPerMemberMinor: bigint): bigint {
  if (typeof amountPerMemberMinor !== "bigint") {
    // Bất biến gọi sai (truyền number/float) — fail-loud, KHÔNG ép kiểu (chống B1).
    throw new DomainError("COLLECTION_AMOUNT_NOT_POSITIVE", AMOUNT_NOT_POSITIVE_MESSAGE);
  }
  if (amountPerMemberMinor <= 0n) {
    throw new DomainError("COLLECTION_AMOUNT_NOT_POSITIVE", AMOUNT_NOT_POSITIVE_MESSAGE);
  }
  return amountPerMemberMinor;
}

/**
 * Lấy "ngày dạng YYYY-MM-DD" của một thời điểm theo timezone chỉ định (ổn định, không phụ thuộc
 * múi giờ máy chủ). Dùng Intl để tránh lệch ngày khi server chạy UTC còn group ở GMT+7.
 */
export function localDateParts(at: Date, timeZone: string): { y: number; m: number; d: number } {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  // en-CA → 'YYYY-MM-DD'
  const [y, m, d] = fmt.format(at).split("-").map((p) => Number.parseInt(p, 10));
  return { y: y!, m: m!, d: d! };
}

/** So sánh 2 bộ (y,m,d): <0 nếu a trước b, 0 nếu cùng ngày, >0 nếu a sau b. */
export function compareYmd(
  a: { y: number; m: number; d: number },
  b: { y: number; m: number; d: number },
): number {
  if (a.y !== b.y) return a.y - b.y;
  if (a.m !== b.m) return a.m - b.m;
  return a.d - b.d;
}

/**
 * `startDate` < HÔM NAY (theo timezone group) ⇒ quá khứ (AC2). So sánh ở mức NGÀY (drop giờ) trong
 * timezone group: đúng "hôm nay" = hợp lệ; chỉ ngày TRƯỚC hôm nay mới reject.
 * `startDate` là Date (đã parse 'YYYY-MM-DD' ở DTO → UTC midnight); `now` cho phép inject (test).
 * Quá khứ ⇒ DomainError REQUIRED_FIELD_MISSING? KHÔNG — dùng code riêng để message rõ; ở đây ta
 * dùng LEDGER_AMOUNT_NOT_POSITIVE-độc-lập là sai. Map message qua detail (code ổn định, message override).
 */
export function assertStartDateNotPast(
  startDate: Date,
  timeZone: string = DEFAULT_GROUP_TIMEZONE,
  now: Date = new Date(),
): void {
  const start = localDateParts(startDate, timeZone);
  const today = localDateParts(now, timeZone);
  if (compareYmd(start, today) < 0) {
    throw new DomainError("COLLECTION_START_DATE_IN_PAST", START_DATE_IN_PAST_MESSAGE);
  }
}

/** Số tuần ISO-8601 của một ngày (1..53). Thuần, theo chuẩn tuần bắt đầu Thứ Hai. */
function isoWeek(d: { y: number; m: number; d: number }): { isoYear: number; week: number } {
  // Dùng UTC để tính ổn định (chỉ là số học lịch, không liên quan timezone hiển thị).
  const date = new Date(Date.UTC(d.y, d.m - 1, d.d));
  const day = date.getUTCDay() || 7; // CN=7
  date.setUTCDate(date.getUTCDate() + 4 - day); // dời tới Thứ Năm cùng tuần ISO
  const isoYear = date.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return { isoYear, week };
}

/**
 * Derive `periodKey` của ĐỢT ĐẦU từ `startDate` theo tần suất (idempotency identity = scheduleId+periodKey):
 *  - monthly  → 'YYYY-MM'        (vd '2026-06')
 *  - quarterly→ 'YYYY-Qn'        (vd '2026-Q2')
 *  - yearly   → 'YYYY'           (vd '2026')
 *  - weekly   → 'YYYY-Www'       (ISO week, vd '2026-W27')
 * periodKey tính theo timezone group (cùng "ngày" với validate quá khứ) để nhất quán biên kỳ.
 */
export function derivePeriodKey(
  frequency: CollectionFrequency,
  startDate: Date,
  timeZone: string = DEFAULT_GROUP_TIMEZONE,
): string {
  const { y, m, d } = localDateParts(startDate, timeZone);
  const yyyy = String(y).padStart(4, "0");
  const mm = String(m).padStart(2, "0");
  switch (frequency) {
    case "monthly":
      return `${yyyy}-${mm}`;
    case "quarterly": {
      const quarter = Math.floor((m - 1) / 3) + 1;
      return `${yyyy}-Q${quarter}`;
    }
    case "yearly":
      return yyyy;
    case "weekly": {
      const { isoYear, week } = isoWeek({ y, m, d });
      return `${String(isoYear).padStart(4, "0")}-W${String(week).padStart(2, "0")}`;
    }
    // US-E23.F3-0333-S07 (@UC9531): năm học thu THEO THÁNG (9 kỳ T9→T5), nên periodKey cùng dạng
    // 'YYYY-MM' với monthly. 🔴 PHẢI MANG NĂM: thiếu năm thì kỳ T9/2026 và T9/2027 trùng key, mà
    // idempotency dùng (scheduleId, periodKey) làm identity ⇒ coi hai kỳ là MỘT ⇒ mất một kỳ thu.
    // Danh sách kỳ hợp lệ do `schoolYearPeriods` quyết (bỏ tháng hè) — hàm này chỉ đặt TÊN kỳ.
    case "school_year":
      return `${yyyy}-${mm}`;
    // US-E16.F3-0688 (@UC0050): học kỳ cũng khoá theo THÁNG BẮT ĐẦU kỳ ⇒ cùng dạng 'YYYY-MM'.
    // Mang năm vì đúng lý do ghi ở `school_year` ngay trên: HK1/2026 và HK1/2027 trùng key thì
    // identity `(scheduleId, periodKey)` coi hai kỳ là MỘT ⇒ mất một kỳ thu. Danh sách kỳ hợp lệ do
    // `semesterPeriods` quyết (hè không có kỳ) — hàm này chỉ đặt TÊN kỳ.
    case "semester":
      return `${yyyy}-${mm}`;
    default: {
      // Bất biến: frequency đã validate ở biên. Nhánh này không reachable runtime.
      const exhaustive: never = frequency;
      throw new DomainError("REQUIRED_FIELD_MISSING", `frequency không hợp lệ: ${String(exhaustive)}`);
    }
  }
}

/** recurrence JSON lưu vào CollectionSchedule.recurrence. weekly kèm `weekday` (1=Thứ Hai..7=CN). */
export interface RecurrenceConfig {
  readonly freq: CollectionFrequency;
  readonly weekday?: number;
  /**
   * US-E23.F3-0333 (@UC9530) — lặp mỗi N đơn vị. LUÔN ghi tường minh khi tạo mới; lịch CŨ trong DB
   * không có khoá này ⇒ reader đọc `?? 1` ⇒ 0 regress.
   */
  readonly interval?: number;
  /** CHỈ có nghĩa với `weekly`: các thứ sinh mốc (ISO 1..7), đã sort + dedupe. */
  readonly byWeekday?: readonly number[];
  /**
   * 🔴 US-E16.F3-0688 (@UC0050) — CHỈ có nghĩa với `semester`: tháng bắt đầu từng học kỳ, THEO THỨ TỰ
   * người dùng khai. **KHÔNG sort ở đây** — thứ tự đã được `assertSemesterStartMonths` kiểm là hợp lệ
   * trong cửa sổ năm học, và sort lại theo số học sẽ phá đúng ca bắc cầu tháng 12 ([9,1] → [1,9]).
   */
  readonly semesterStartMonths?: readonly number[];
  /** CHỈ với `semester`: tháng KẾT THÚC kỳ cuối, do người tạo khai (QĐ 03/09). */
  readonly semesterEndMonth?: number;
}

/**
 * Build `recurrence` JSON từ tần suất (+ thứ nếu weekly — AC3 dropdown chọn thứ). weekday 1..7
 * (ISO: 1=Thứ Hai..7=Chủ Nhật). Non-weekly mà truyền weekday ⇒ bỏ qua (recurrence chỉ giữ freq).
 * weekly mà weekday ngoài 1..7 hoặc thiếu ⇒ DomainError (biên đã chặn; double-check fail-closed).
 */
export function buildRecurrence(
  frequency: CollectionFrequency,
  weekday?: number,
  opts?: {
    readonly interval?: number;
    readonly byWeekday?: readonly number[];
    readonly semesterStartMonths?: readonly number[];
    /** Tháng KẾT THÚC kỳ cuối — người tạo khai; cần để kiểm mốc cuối không rơi ra ngoài. */
    readonly semesterEndMonth?: number;
  },
): RecurrenceConfig {
  // 🔴 US-E16.F3-0688 (@UC0050) — mốc học kỳ: BẮT BUỘC với `semester`, CẤM với tần suất khác.
  // Đặt TRƯỚC mọi nhánh khác vì `semester` không đi qua đường `weekday`/`byWeekday` bên dưới.
  const semesterStartMonths = opts?.semesterStartMonths;
  if (frequency === "semester") {
    if (!semesterStartMonths) {
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        "Chu kỳ theo học kỳ cần chọn tháng bắt đầu cho từng học kỳ",
      );
    }
    // Kiểm ĐẦY ĐỦ ở domain (số lượng, kiểu, trùng, thứ tự trong cửa sổ, biên năm học) — biên zod chỉ
    // chặn được phần không cần biết `schoolYearEndMonth`.
    assertSemesterStartMonths(semesterStartMonths, opts?.semesterEndMonth);
    const interval = opts?.interval ?? 1;
    if (interval !== 1) {
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        "Chu kỳ theo học kỳ không lặp nhiều năm một lần",
      );
    }
    // KHÔNG sort: thứ tự người dùng khai đã được kiểm hợp lệ; sort theo số học phá ca [9,1].
    return {
      freq: frequency,
      interval,
      semesterStartMonths: [...semesterStartMonths],
      ...(opts?.semesterEndMonth !== undefined ? { semesterEndMonth: opts.semesterEndMonth } : {}),
    };
  }
  if (semesterStartMonths !== undefined) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Chỉ chu kỳ theo học kỳ mới chọn được tháng bắt đầu học kỳ",
    );
  }

  // US-E23.F3-0333 (@UC9530): interval LUÔN ghi tường minh (mặc định 1) ⇒ lịch mới không dựa vào
  // dấu `??` của reader. Fail-loud nếu sai, KHÔNG kẹp thầm về 1 (cấu hình sai phải nổ ở người nhập).
  const interval = opts?.interval ?? 1;
  if (!Number.isInteger(interval) || interval < 1) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Số chu kỳ lặp phải là số nguyên ≥ 1 (nhận ${String(interval)})`,
    );
  }

  const byWeekday = opts?.byWeekday;
  if (byWeekday !== undefined) {
    if (frequency !== "weekly") {
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        "Chỉ chu kỳ theo tuần mới chọn được các thứ trong tuần",
      );
    }
    if (byWeekday.length === 0) {
      throw new DomainError("REQUIRED_FIELD_MISSING", "Chọn ít nhất một thứ trong tuần");
    }
    for (const wd of byWeekday) {
      if (!Number.isInteger(wd) || wd < 1 || wd > 7) {
        throw new DomainError(
          "REQUIRED_FIELD_MISSING",
          "Thứ trong tuần phải từ 1 (Thứ Hai) đến 7 (Chủ Nhật)",
        );
      }
    }
    // Sort + dedupe MỘT LẦN tại nơi GHI ⇒ reader/worker không phải chuẩn hoá lại (và không có cơ hội
    // chuẩn hoá khác nhau ở hai chỗ).
    return {
      freq: frequency,
      interval,
      byWeekday: [...new Set(byWeekday)].sort((a, b) => a - b),
    };
  }

  if (frequency === "weekly") {
    if (weekday === undefined) {
      throw new DomainError("REQUIRED_FIELD_MISSING", "Chu kỳ tuần cần chọn thứ trong tuần");
    }
    if (!Number.isInteger(weekday) || weekday < 1 || weekday > 7) {
      throw new DomainError("REQUIRED_FIELD_MISSING", "Thứ trong tuần phải từ 1 (Thứ Hai) đến 7 (Chủ Nhật)");
    }
    return { freq: frequency, weekday, interval };
  }
  return { freq: frequency, interval };
}

/**
 * US-E23.F15-0347-S02 (@UC9540) — ĐỌC ngược cột `CollectionSchedule.recurrence` (Json) → `{interval,
 * byWeekday?}`. Cặp đôi của `buildRecurrence` (đường GHI).
 *
 * 🔴 **MỘT parser cho MỌI người đọc.** Worker đọc cột này để sinh `member_dues` (⇒ sinh TIỀN); admin
 * oversight đọc cùng cột để hiện luật lặp cho OPS. Hai parser riêng ⇒ hai cách hiểu cùng một hàng DB
 * ⇒ admin nói một đằng engine chạy một nẻo — đúng họ bệnh §3.8 *"reuse, không cơ chế thứ hai"*, lần
 * này ở tầng ĐỌC. Hàm này vốn là bản private trong `recurring-collection.job.ts`, nay nâng lên domain
 * để cả hai phía dùng chung; hành vi giữ NGUYÊN VĂN (worker 0 regress).
 *
 * 🔴 Giá trị lạ/thiếu ⇒ về mặc định AN TOÀN (`interval=1`, không `byWeekday`) = đúng hành vi trước
 * task 0333. Chọn hướng này thay vì ném vì worker là job NỀN chạy hằng ngày: một hàng hỏng KHÔNG được
 * chặn cả lượt sinh kỳ của mọi quỹ khác. Cấu hình hỏng chỉ vào được DB nếu vượt cả contract (400 ở
 * biên) LẪN CHECK constraint ⇒ nhánh này là lưới cuối, không phải đường thường.
 *
 * `byWeekday` lọc xong còn rỗng ⇒ **bỏ hẳn khoá**, không trả `[]`: một cách biểu diễn duy nhất cho
 * "không chọn thứ nào", để caller không phải viết thêm nhánh `length === 0` (rồi có caller quên).
 *
 * PURE — không I/O, không throw.
 */
export function parseRecurrenceConfig(raw: unknown): {
  interval: number;
  byWeekday?: readonly number[];
  semesterStartMonths?: readonly number[];
  semesterEndMonth?: number;
} {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return { interval: 1 };
  const obj = raw as Record<string, unknown>;
  const interval =
    typeof obj.interval === "number" && Number.isInteger(obj.interval) && obj.interval >= 1
      ? obj.interval
      : 1;
  const byWeekday = Array.isArray(obj.byWeekday)
    ? obj.byWeekday.filter((v): v is number => typeof v === "number" && v >= 1 && v <= 7)
    : undefined;
  // 🔴 US-E16.F3-0688 (@UC0050) — đọc lại mốc học kỳ đã lưu. GIỮ NGUYÊN THỨ TỰ (không sort): thứ tự
  // là dữ liệu, không phải hình thức — [9,1] và [1,9] mô tả hai năm học khác nhau.
  //
  // ⚠️ Lọc phần tử rác nhưng KHÔNG tự vá độ dài: một mảng còn 1 phần tử sau lọc sẽ bị
  // `assertSemesterStartMonths` ở tầng dùng chặn lại và nổ RÕ, thay vì lặng lẽ chạy với một học kỳ.
  const semesterStartMonths = Array.isArray(obj.semesterStartMonths)
    ? obj.semesterStartMonths.filter(
        (v): v is number => typeof v === "number" && Number.isInteger(v) && v >= 1 && v <= 12,
      )
    : undefined;
  const semesterEndMonth =
    typeof obj.semesterEndMonth === "number" &&
    Number.isInteger(obj.semesterEndMonth) &&
    obj.semesterEndMonth >= 1 &&
    obj.semesterEndMonth <= 12
      ? obj.semesterEndMonth
      : undefined;
  return {
    interval,
    ...(byWeekday && byWeekday.length > 0 ? { byWeekday } : {}),
    ...(semesterStartMonths && semesterStartMonths.length > 0 ? { semesterStartMonths } : {}),
    ...(semesterEndMonth !== undefined ? { semesterEndMonth } : {}),
  };
}

/**
 * True nếu lịch KHÔNG được sinh đợt mới ⇒ generateInstanceIfDue/worker phải SKIP:
 *  - paused (0035 AC5 — tạm dừng, resume lại sinh tiếp).
 *  - cancelled (US-E16.F3-0184 — huỷ hẳn, KHÔNG bao giờ sinh kỳ tương lai; lịch sử kỳ cũ giữ nguyên).
 * Chỉ `active` mới sinh. Giữ tương thích gọi cũ (chỉ mở rộng thêm nhánh cancelled).
 */
export function shouldSkipGeneration(scheduleStatus: string): boolean {
  return scheduleStatus === SCHEDULE_STATUS.PAUSED || scheduleStatus === SCHEDULE_STATUS.CANCELLED;
}

// ============================================================
// US-E16.F3-0184-recurring-advanced — reprice / skip / member-freeze (pure)
// ============================================================

/**
 * Một mốc reprice: TỪ kỳ `effectiveFromPeriodKey` (inclusive) trở đi, giá suất/người = `amountMinor`.
 * amountMinor = BigInt minor-units (B1 — KHÔNG float). Về nghiệp vụ append-only (không sửa kỳ đã sinh).
 */
export interface RepricePeriod {
  readonly effectiveFromPeriodKey: string;
  readonly amountMinor: bigint;
}

/**
 * @UC9238 — reprice KHÔNG hồi tố. Giá của `periodKey` = amount của mốc reprice MỚI NHẤT có
 * `effectiveFromPeriodKey <= periodKey`; không có mốc nào phủ (kỳ trước reprice đầu tiên) ⇒ `basePriceMinor`.
 *
 * periodKey so sánh bằng string ordinal (derivePeriodKey đã zero-pad — 'YYYY-MM' / 'YYYY-Qn' /
 * 'YYYY-Www' / 'YYYY' đều tăng đơn điệu theo thời gian trong CÙNG tần suất ⇒ so sánh chuỗi ổn định).
 * Pure, BigInt. `basePriceMinor`/`amountMinor` không phải bigint ⇒ fail-loud (chống B1).
 */
export function resolvePriceForPeriod(
  repriceHistory: readonly RepricePeriod[],
  periodKey: string,
  basePriceMinor: bigint,
): bigint {
  if (typeof basePriceMinor !== "bigint") {
    throw new DomainError("COLLECTION_AMOUNT_NOT_POSITIVE", AMOUNT_NOT_POSITIVE_MESSAGE);
  }
  let best: RepricePeriod | null = null;
  for (const r of repriceHistory) {
    if (typeof r.amountMinor !== "bigint") {
      throw new DomainError("COLLECTION_AMOUNT_NOT_POSITIVE", AMOUNT_NOT_POSITIVE_MESSAGE);
    }
    // Mốc có hiệu lực với kỳ này (from <= periodKey) và KHÔNG cũ hơn best hiện tại: `>=` ⇒ hai dòng cùng
    // mốc thì dòng SAU (ghi sau) thắng — "lấy thay đổi mới nhất" (US-AE4.F7-1003; trước đây `>` giữ dòng ĐẦU).
    if (
      r.effectiveFromPeriodKey <= periodKey &&
      (best === null || r.effectiveFromPeriodKey >= best.effectiveFromPeriodKey)
    ) {
      best = r;
    }
  }
  return best ? best.amountMinor : basePriceMinor;
}

/**
 * US-AE4.F7-1003 (@UC9636) — ghi một mốc giá vào sổ giá: cùng `effectiveFromPeriodKey` ⇒ THAY (mỗi kỳ đúng một
 * giá; dòng trùng do dữ liệu cũ cũng được gộp), khác mốc ⇒ nối cuối. Không sửa mảng đầu vào. Pure, BigInt.
 * Lịch sử "ai đổi, khi nào, giá cũ" không mất: nó nằm ở `audit_log`, sổ giá chỉ giữ giá đang hiệu lực.
 */
export function upsertRepriceEntry(
  history: readonly RepricePeriod[],
  entry: RepricePeriod,
): RepricePeriod[] {
  const kept = history.filter((r) => r.effectiveFromPeriodKey !== entry.effectiveFromPeriodKey);
  const at = history.findIndex((r) => r.effectiveFromPeriodKey === entry.effectiveFromPeriodKey);
  if (at === -1) return [...kept, entry];
  // Giữ chỗ của dòng đầu cùng mốc để thứ tự các mốc khác không xê dịch.
  const out = [...kept];
  out.splice(Math.min(at, out.length), 0, entry);
  return out;
}

/**
 * US-AE4.F7-1003 (@UC9636) — mốc "từ kỳ sau" khi đổi giá đợt định kỳ: `periodKey` của NGÀY KẾT THÚC nửa mở của
 * kỳ đã sinh gần nhất (= ngày đầu kỳ kế). Dùng `derivePeriodKey` có sẵn — KHÔNG có phép tính kỳ thứ hai.
 * Với chu kỳ có khoảng trống (năm học, học kỳ) mốc có thể là một tháng không có kỳ (vd 2027-06 sau kỳ cuối
 * năm học) — vẫn đúng vì `resolvePriceForPeriod` chỉ cần mốc nằm SAU kỳ đã sinh và ≤ kỳ thật kế tiếp.
 */
export function periodKeyAfter(
  frequency: CollectionFrequency,
  latestPeriodEnd: Date,
  timeZone: string = DEFAULT_GROUP_TIMEZONE,
): string {
  return derivePeriodKey(frequency, latestPeriodEnd, timeZone);
}

/** @UC9239 — kỳ `periodKey` có bị đánh dấu skip không (cả nhóm đi vắng ⇒ worker không sinh due). */
export function isPeriodSkipped(skippedKeys: readonly string[], periodKey: string): boolean {
  return skippedKeys.includes(periodKey);
}

/** Freeze nghĩa vụ 1 member: khoảng [fromPeriodKey, toPeriodKey] (toPeriodKey NULL = mở, còn freeze). */
export interface MemberFreeze {
  readonly memberId: string;
  readonly fromPeriodKey: string;
  readonly toPeriodKey: string | null;
}

/**
 * @UC9240 — member `memberId` có bị freeze nghĩa vụ ở `periodKey` không. True nếu tồn tại freeze của
 * member này phủ periodKey: `fromPeriodKey <= periodKey` VÀ (`toPeriodKey` NULL HOẶC `periodKey <= toPeriodKey`).
 * Nhiều freeze cùng member = union (bất kỳ khoảng nào phủ ⇒ true). So sánh string ordinal (như reprice).
 */
export function isMemberFrozen(
  freezes: readonly MemberFreeze[],
  memberId: string,
  periodKey: string,
): boolean {
  return freezes.some(
    (f) =>
      f.memberId === memberId &&
      f.fromPeriodKey <= periodKey &&
      (f.toPeriodKey === null || periodKey <= f.toPeriodKey),
  );
}

// ============================================================
// US-E24.F15-0364 — PHẠM VI THÀNH VIÊN của lịch định kỳ (@UC9616). QĐ PO 08/08/2026.
// ============================================================

/**
 * @UC9616 — LUẬT DUY NHẤT "ai được áp lịch định kỳ này". Giao tập `activeMemberIds` (người đủ điều
 * kiện sinh due ở KỲ NÀY) với `scopeMemberIds` (phạm vi lưu trên lịch).
 *
 * 🔴 HÀM NÀY LÀ **MỘT NGUỒN LUẬT** cho HAI cửa ghi: đợt ĐẦU do **API** sinh
 * (`collection-schedule.service.ts` → `generateFirstInstanceInTx`), kỳ SAU do **WORKER** sinh
 * (`recurring-collection.job.ts`). Nhân bản luật sang một trong hai ⇒ kỳ đầu và kỳ sau đòi tiền HAI
 * TẬP NGƯỜI KHÁC NHAU — sai lệch tiền, im lặng, phát hiện sau nhiều tháng. Đây đúng mẫu hình gốc
 * CLAUDE.md §5 *"guard canh nhầm chỗ"*: luật gắn ở MỘT cửa trong khi có HAI cửa ghi.
 * Guard khoá điều này: `collection-schedule-member-scope.guard.spec.ts` (@UC9619).
 *
 * NGỮ NGHĨA (QĐ PO 08/08/2026 — chọn **danh sách TRẮNG**):
 *  - `scopeMemberIds` RỖNG ⇒ **không giới hạn** ⇒ trả nguyên `activeMemberIds` (§3.8
 *    *"default ⇒ TẮT ⇒ 0 REGRESS"* — lịch tạo trước task này không đổi một hành vi nào).
 *  - Có phạm vi ⇒ CHỈ người vừa trong phạm vi VỪA đủ điều kiện kỳ này. Giao rỗng ⇒ `[]`
 *    (🔴 KHÔNG fallback "thu cả nhóm": biến một cấu hình lỗi thành đòi tiền 40 người).
 *  - Người vào nhóm SAU khi lịch tạo ⇒ ngoài phạm vi ⇒ **KHÔNG** bị áp lịch (cái giá PO đã chấp nhận
 *    có chủ đích — máy không tự đặt nghĩa vụ cho người chưa có mặt lúc nhóm quyết, §3.7).
 *    🔴 **ĐO 08/08/2026: KHÔNG có endpoint nào sửa `member_ids` của lịch đã tạo.** Route trên lịch
 *    chỉ có pause/resume/generate/reprice(chỉ số tiền)/skip/cancel/freeze-member. Lối duy nhất hiện
 *    nay là **cancel + tạo lịch mới** (mất history, và tạo mới lại sinh đợt đầu đồng bộ).
 *    ⇒ ĐỪNG viết tài liệu/UI hứa "sửa lịch, thêm tay" — đường đó CHƯA tồn tại. Đã ghi vào bảng
 *    *"API còn THIẾU"* của `docs/SCREEN_API_MAP.md`; cần task riêng.
 *  - Phạm vi neo `groupMemberId`, KHÔNG neo `status`: member rời `active` rồi quay lại VẪN trong phạm
 *    vi; kỳ nào không `active` thì caller không đưa vào `activeMemberIds` ⇒ kỳ đó không có due.
 *
 * Thuần, không mutate đầu vào. Thứ tự kết quả theo `activeMemberIds` (ổn định, không theo thứ tự người
 * nhập). Id trùng trong phạm vi ⇒ member CHỈ xuất hiện một lần (biên đã chặn trùng, đây là fail-safe
 * chống nhân đôi nghĩa vụ với dữ liệu ghi tay).
 */
export function resolveScheduleMemberScope(
  activeMemberIds: readonly string[],
  scopeMemberIds: readonly string[],
): string[] {
  // 🔴 FAIL-CLOSED khi caller quên `select: { memberIds: true }`. Cột DB là `NOT NULL DEFAULT '{}'`
  // nên `undefined` CHỈ xảy ra khi một đường ghi mới quên select — và lúc đó hai lựa chọn là:
  //   · coi như `[]` ⇒ "không giới hạn" ⇒ **đòi tiền CẢ NHÓM** trên một lịch chỉ dành cho 3 người,
  //     im lặng, đúng kiểu sai tiền mà cả task này sinh ra để chặn;
  //   · NỔ ⇒ job bắt lỗi từng lịch (`generateForSchedule` try/catch) ⇒ kỳ đó **không sinh due**.
  // Chọn vế thứ hai: *thiếu-thu sửa bằng một cú bấm; thừa-thu phải REVERSAL + xin lỗi cả nhóm.*
  // Ném DomainError (không để `TypeError` trần) để log nói đúng nguyên nhân thay vì "cannot read
  // properties of undefined" — người trực đọc log lúc 2h sáng cần câu đầu tiên đã chỉ đúng chỗ.
  if (!Array.isArray(scopeMemberIds)) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Thiếu phạm vi thành viên của lịch thu (memberIds) — đường sinh due quên select cột này.",
    );
  }
  if (scopeMemberIds.length === 0) return [...activeMemberIds];
  const scope = new Set(scopeMemberIds);
  return activeMemberIds.filter((id) => scope.has(id));
}

/** Format {y,m,d} → 'YYYY-MM-DD' (date-only, zero-pad). */
export function formatYmd(p: { y: number; m: number; d: number }): string {
  const yyyy = String(p.y).padStart(4, "0");
  const mm = String(p.m).padStart(2, "0");
  const dd = String(p.d).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Cộng số THÁNG vào (y,m,d) — clamp ngày cuối tháng (vd 31 → ngày cuối nếu tháng đích ngắn hơn). */
export function addMonths(p: { y: number; m: number; d: number }, months: number): {
  y: number;
  m: number;
  d: number;
} {
  const zeroBased = p.m - 1 + months;
  const y = p.y + Math.floor(zeroBased / 12);
  const m = (zeroBased % 12 + 12) % 12; // 0..11
  // Ngày cuối tháng đích (Date.UTC với day 0 của tháng kế = ngày cuối tháng hiện tại).
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const d = Math.min(p.d, lastDay);
  return { y, m: m + 1, d };
}

/** Cộng số NGÀY vào (y,m,d) qua UTC (số học lịch ổn định). */
export function addDays(p: { y: number; m: number; d: number }, days: number): {
  y: number;
  m: number;
  d: number;
} {
  const date = new Date(Date.UTC(p.y, p.m - 1, p.d));
  date.setUTCDate(date.getUTCDate() + days);
  return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() };
}

/** Số THÁNG mỗi chu kỳ cho tần suất theo-tháng; null nếu là weekly (theo-ngày). */
function monthsPerPeriod(frequency: CollectionFrequency): number | null {
  switch (frequency) {
    case "weekly":
      return null;
    case "monthly":
      return 1;
    case "quarterly":
      return 3;
    case "yearly":
      return 12;
    // 🔴 `school_year` KHÔNG cuộn đều đặn được: nó có khoảng trống (tháng hè). Trả 1 tháng ở đây sẽ
    // khiến `computeNextDate` sinh cả kỳ tháng 6/7/8 — tức thu tiền những tháng cả nhóm đã nghỉ.
    // Mốc kỳ năm học do `schoolYearPeriods` (school-year.ts) quyết; caller PHẢI đi đường đó.
    case "school_year":
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        "Chu kỳ theo năm học không cuộn đều đặn — dùng schoolYearPeriods() để lấy mốc kỳ",
      );
    // 🔴 US-E16.F3-0688: học kỳ CŨNG không cuộn đều đặn — và còn tệ hơn `school_year` ở chỗ các kỳ
    // KHÔNG dài bằng nhau (HK1 4 tháng, HK2 5 tháng với mốc [T9,T1] + năm học hết T5). Trả một con
    // số ở đây là bịa ra một chu kỳ đều không tồn tại ⇒ `computeNextDate` sinh sai mốc, và sai mốc
    // nghĩa là đòi tiền cả nhóm vào tháng không ai chờ. Caller PHẢI đi qua `semesterPeriods`.
    case "semester":
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        "Chu kỳ theo học kỳ không cuộn đều đặn — dùng semesterPeriods() để lấy mốc kỳ",
      );
    default: {
      const exhaustive: never = frequency;
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        `frequency không hợp lệ: ${String(exhaustive)}`,
      );
    }
  }
}

/**
 * Occurrence thứ `n` (n>=0) tính TỪ `anchor` (đợt 0 = anchor). Anchored — clamp ngày-cuối-tháng
 * LẠI TỪ ĐẦU mỗi lần từ `anchor.d` GỐC, KHÔNG cuộn dồn từ giá trị đã clamp (tránh ngày-31 → kẹt-28
 * sau khi qua tháng ngắn). DRY — dùng trong computeNextDate.
 */
function nthOccurrence(
  anchor: { y: number; m: number; d: number },
  frequency: CollectionFrequency,
  n: number,
): { y: number; m: number; d: number } {
  const months = monthsPerPeriod(frequency);
  if (months === null) return addDays(anchor, 7 * n); // weekly
  return addMonths(anchor, months * n); // addMonths clamp từ anchor.d gốc mỗi lần
}

/**
 * Tính NGÀY đợt thu KẾ TIẾP ('YYYY-MM-DD') từ `startDate` cuộn theo `frequency` cho tới khi >= HÔM NAY
 * (theo timezone group — nhất quán `derivePeriodKey`/`assertStartDateNotPast`). Read-only hiển thị (AC1).
 *  - startDate >= hôm nay ⇒ chính startDate (đợt đầu chưa tới).
 *  - startDate quá khứ ⇒ cuộn +1 chu kỳ cho tới occurrence đầu tiên >= hôm nay (inclusive).
 *  - `dueDate` (hạn đóng) nếu set & còn tương lai (>= hôm nay) & SỚM hơn occurrence ⇒ trả dueDate
 *    (hạn gần hơn là mốc người dùng quan tâm). dueDate quá khứ ⇒ bỏ qua.
 *
 * Pure (DRY — B3): api list/detail và worker P8.5 dùng chung. KHÔNG money math. Cap vòng lặp chống
 * input bệnh lý (startDate quá xa quá khứ): tối đa MAX_PERIODS_LOOKAHEAD bước.
 */
const MAX_PERIODS_LOOKAHEAD = 4000;

export function computeNextDate(
  frequency: CollectionFrequency,
  startDate: Date,
  dueDate: Date | null = null,
  now: Date = new Date(),
  timeZone: string = DEFAULT_GROUP_TIMEZONE,
): string {
  const today = localDateParts(now, timeZone);
  const anchor = localDateParts(startDate, timeZone);

  // Cuộn occurrence thứ n (anchored từ startDate gốc) cho tới >= hôm nay. KHÔNG dồn ngày đã clamp.
  let n = 0;
  let cursor = anchor;
  while (compareYmd(cursor, today) < 0 && n < MAX_PERIODS_LOOKAHEAD) {
    n += 1;
    cursor = nthOccurrence(anchor, frequency, n);
  }

  // dueDate: nếu set, còn >= hôm nay, và sớm hơn occurrence kế ⇒ là mốc gần hơn.
  if (dueDate) {
    const due = localDateParts(dueDate, timeZone);
    if (compareYmd(due, today) >= 0 && compareYmd(due, cursor) < 0) {
      return formatYmd(due);
    }
  }

  return formatYmd(cursor);
}

/** Kết quả due-check của worker: kỳ HIỆN TẠI cần sinh nếu hôm nay là ngày occurrence; null nếu không. */
export interface DuePeriod {
  /** periodKey của kỳ due (identity đợt cùng scheduleId — B4 idempotent). */
  readonly periodKey: string;
  /** Ngày occurrence 'YYYY-MM-DD' rơi đúng hôm nay (theo timezone group). */
  readonly occurrenceDate: string;
}

/**
 * US-E12.F2-0069 (@UC0070) — worker P8.5 chạy HẰNG NGÀY hỏi: "hôm nay có phải ngày sinh đợt cho lịch
 * này không?". Occurrence lịch (theo `frequency` cuộn từ `startDate`, timezone group) RƠI ĐÚNG hôm nay
 * ⇒ due → trả periodKey của kỳ HIỆN TẠI (derive từ ngày occurrence, KHÔNG từ startDate — đó là kỳ đầu).
 * Không rơi hôm nay (chưa tới / đã qua trong ngày khác) ⇒ null (KHÔNG sinh).
 *
 * Pure (DRY — B3): reuse `computeNextDate` (occurrence >= hôm nay) + `derivePeriodKey`. `dueDate` (hạn
 * ĐÓNG) KHÔNG tham gia due-detection — chỉ occurrence LỊCH quyết định NGÀY SINH (dueDate là mốc hạn nộp,
 * không phải mốc tạo đợt); truyền null vào computeNextDate để dueDate không kéo mốc về sớm hơn occurrence.
 * KHÔNG money math. Clock qua `now` (inject test). Idempotent: cùng input → cùng kết quả.
 */
export function resolveDuePeriod(
  frequency: CollectionFrequency,
  startDate: Date,
  dueDate: Date | null = null,
  now: Date = new Date(),
  timeZone: string = DEFAULT_GROUP_TIMEZONE,
): DuePeriod | null {
  const today = formatYmd(localDateParts(now, timeZone));
  // Occurrence lịch kế tiếp >= hôm nay (dueDate=null: hạn đóng KHÔNG dời mốc sinh).
  const occurrence = computeNextDate(frequency, startDate, null, now, timeZone);
  if (occurrence !== today) return null;

  // Occurrence rơi đúng hôm nay ⇒ due. periodKey của KỲ NÀY (parse occurrence → Date UTC → derive).
  void dueDate; // dueDate không ảnh hưởng due-detection (giữ chữ ký đối xứng computeNextDate).
  const [y, m, d] = occurrence.split("-").map((p) => Number.parseInt(p, 10));
  const occurrenceUtc = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
  const periodKey = derivePeriodKey(frequency, occurrenceUtc, timeZone);
  return { periodKey, occurrenceDate: occurrence };
}

/** Kỳ NỬA MỞ `[periodStart, periodEnd)` (UTC midnight Dates) — feed thẳng presenceDaysInPeriod/prorata. */
export interface PeriodRange {
  /** Ngày ĐẦU kỳ (inclusive), UTC midnight. */
  readonly periodStart: Date;
  /** Ngày ĐẦU kỳ SAU (exclusive), UTC midnight — nửa mở. */
  readonly periodEnd: Date;
}

/**
 * US-E21.F6-0218 (@UC9358) — derive biên kỳ `[periodStart, periodEnd)` NỬA MỞ (UTC) từ một NGÀY
 * occurrence rơi TRONG kỳ, theo tần suất. Feed thẳng vào `presenceDaysInPeriod`/`proRataShare`/
 * `proRataAllocate` (prorata pro-rata theo NGÀY hiện diện — mắt xích B: worker sinh due prorata).
 *
 *  - monthly  → [1 của tháng, 1 của tháng sau)          (vd occurrence 2026-03-16 → [2026-03-01, 2026-04-01)).
 *  - quarterly→ [đầu quý, đầu quý sau)                    (Q1=[1/1,1/4), Q2=[1/4,1/7), ...).
 *  - yearly   → [1/1, 1/1 năm sau).
 *  - weekly   → [Thứ Hai ISO của tuần, +7 ngày)          (ISO week: bắt đầu Thứ Hai).
 *
 * Pure (KHÔNG money, KHÔNG DB). UTC midnight ổn định TZ (khớp @db.Date). Nhất quán biên nửa-mở với
 * derivePeriodKey (cùng khái niệm "kỳ").
 */
/**
 * Cấu hình kỳ HỌC KỲ — bắt buộc khi `frequency === "semester"`.
 *
 * 🔴 Vì sao là tham số chứ không suy từ `occurrenceDate`: học kỳ KHÔNG dài bằng nhau và biên của nó
 * do người tạo đợt thu chọn, nên từ một ngày lẻ không thể suy ngược ra kỳ. Mà hàm này feed thẳng vào
 * `proRataShare` — đoán sai biên kỳ nghĩa là **chia sai tiền theo ngày hiện diện**.
 */
export interface SemesterRangeConfig {
  readonly semesterStartMonths: readonly number[];
  /** Tháng KẾT THÚC kỳ cuối — do người tạo đợt thu khai (QĐ 03/09), không lấy từ config nhóm. */
  readonly semesterEndMonth?: number;
}

/**
 * Biên `[start, end)` của học kỳ chứa `occurrenceDate`.
 *
 * 🔴 ĐẶT Ở ĐÂY, KHÔNG ở `semester.ts`: `semester.ts` đã import `localDateParts` từ file này, nên
 * import ngược lại tạo **vòng ESM**. `tsc` KHÔNG bắt được vòng đó — nó nổ lúc chạy dưới dạng
 * `undefined` giữa đường, đúng cảnh báo đã ghi ở `collection-schedules.ts` (contracts).
 *
 * Toán mốc: mọi thứ quy về OFFSET tính từ đầu cửa sổ năm học, rồi mới đổi ngược ra (năm, tháng) —
 * so tháng thô sẽ sai ngay ở cửa sổ bắc cầu qua tháng 12 (T1 < T9 về số, nhưng đứng SAU trong năm học).
 */
function resolveSemesterRange(occurrenceDate: Date, config: SemesterRangeConfig): PeriodRange {
  const months = config.semesterStartMonths;
  const endMonth = config.semesterEndMonth ?? 5;
  if (months.length === 0) {
    throw new DomainError("REQUIRED_FIELD_MISSING", "Thiếu mốc tháng học kỳ");
  }
  const firstMonth = months[0]!;
  const monthOf = (k: number): number => ((firstMonth - 1 + k) % 12) + 1;
  const yearOffsetOf = (k: number): number => Math.floor((firstMonth - 1 + k) / 12);
  const offsetOf = (month: number): number => (month - firstMonth + 12) % 12;

  const offsets = months.map(offsetOf);
  // Cùng luật với `semesterPeriods`, PHẢI khớp từng tháng — lệch giữa hai hàm nghĩa là biên PRO-RATA
  // khác với kỳ đã sinh ⇒ chia sai tiền theo ngày hiện diện.
  const endOffset = offsetOf(endMonth);
  const occMonth = occurrenceDate.getUTCMonth() + 1;
  const occOffset = offsetOf(occMonth);

  // Ngoài cửa sổ năm học (hè) ⇒ KHÔNG kỳ nào chứa ngày này. Ném thay vì trả một khoảng bịa: đây là
  // đầu vào của pro-rata, một khoảng sai đi thẳng vào số tiền phải đóng.
  if (occOffset > endOffset) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Ngày ${occurrenceDate.toISOString().slice(0, 10)} nằm ngoài năm học — không thuộc học kỳ nào`,
    );
  }

  let index = 0;
  for (let i = 0; i < offsets.length; i++) {
    if (offsets[i]! <= occOffset) index = i;
  }
  const startOffset = offsets[index]!;
  // Kỳ cuối kết thúc HẾT tháng `endMonth` ⇒ biên nửa-mở là đầu tháng kế tiếp (`endOffset + 1`).
  const endExclusiveOffset = index + 1 < offsets.length ? offsets[index + 1]! : endOffset + 1;

  const anchorYear = occurrenceDate.getUTCFullYear() - yearOffsetOf(occOffset);
  return {
    periodStart: new Date(
      Date.UTC(anchorYear + yearOffsetOf(startOffset), monthOf(startOffset) - 1, 1),
    ),
    periodEnd: new Date(
      Date.UTC(anchorYear + yearOffsetOf(endExclusiveOffset), monthOf(endExclusiveOffset) - 1, 1),
    ),
  };
}

export function resolvePeriodRange(
  frequency: CollectionFrequency,
  occurrenceDate: Date,
  semesterConfig?: SemesterRangeConfig,
): PeriodRange {
  const y = occurrenceDate.getUTCFullYear();
  const m = occurrenceDate.getUTCMonth(); // 0-index
  const d = occurrenceDate.getUTCDate();

  switch (frequency) {
    case "monthly": {
      const periodStart = new Date(Date.UTC(y, m, 1));
      const periodEnd = new Date(Date.UTC(y, m + 1, 1));
      return { periodStart, periodEnd };
    }
    case "quarterly": {
      const quarterStartMonth = Math.floor(m / 3) * 3;
      const periodStart = new Date(Date.UTC(y, quarterStartMonth, 1));
      const periodEnd = new Date(Date.UTC(y, quarterStartMonth + 3, 1));
      return { periodStart, periodEnd };
    }
    case "yearly": {
      const periodStart = new Date(Date.UTC(y, 0, 1));
      const periodEnd = new Date(Date.UTC(y + 1, 0, 1));
      return { periodStart, periodEnd };
    }
    case "weekly": {
      // Thứ Hai ISO của tuần chứa occurrence: getUTCDay 0=CN..6=T7 → ISO 1=T2..7=CN.
      const isoDow = occurrenceDate.getUTCDay() || 7;
      const periodStart = new Date(Date.UTC(y, m, d - (isoDow - 1)));
      const periodEnd = new Date(Date.UTC(y, m, d - (isoDow - 1) + 7));
      return { periodStart, periodEnd };
    }
    // US-E23.F3-0333-S07 (@UC9531): năm học thu THEO THÁNG (9 kỳ T9→T5) ⇒ cửa sổ kỳ = ĐÚNG tháng đó,
    // y hệt `monthly`. Chỗ khác biệt duy nhất của năm học là DANH SÁCH tháng nào có kỳ (bỏ hè) —
    // việc đó do `schoolYearPeriods` quyết, không phải cửa sổ của một kỳ đã biết.
    case "school_year": {
      const periodStart = new Date(Date.UTC(y, m, 1));
      const periodEnd = new Date(Date.UTC(y, m + 1, 1));
      return { periodStart, periodEnd };
    }
    // 🔴 US-E16.F3-0688 (@UC0050) — KỲ HỌC KỲ DÀI NHIỀU THÁNG VÀ KHÔNG ĐỀU NHAU.
    //
    // Với mốc [T9, T1] + năm học hết T5: HK1 = 4 tháng, HK2 = 5 tháng. Sao chép nhánh `school_year`
    // ngay trên (trả đúng một tháng) sẽ khiến `proRataShare` coi kỳ dài 30 ngày trong khi nó dài
    // ~120 ngày ⇒ người vào giữa kỳ bị tính **sai số tiền phải đóng**. Đây là đường TIỀN, nên thiếu
    // cấu hình thì NÉM, tuyệt đối không đoán (§3.8 fail-closed).
    case "semester": {
      if (!semesterConfig) {
        throw new DomainError(
          "REQUIRED_FIELD_MISSING",
          "Chu kỳ theo học kỳ cần mốc tháng học kỳ để tính biên kỳ (semesterConfig)",
        );
      }
      return resolveSemesterRange(occurrenceDate, semesterConfig);
    }
    default: {
      const exhaustive: never = frequency;
      throw new DomainError("REQUIRED_FIELD_MISSING", `frequency không hợp lệ: ${String(exhaustive)}`);
    }
  }
}
